import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Bike, MapPin, Phone, RefreshCw, Wallet } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { DispatchMap } from '@/components/dispatch/DispatchMap'
import { lastSeenLabel, locationFreshness } from '@/lib/riderLocationBuffer'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import { useDashboardStream, type DashboardStreamEvent } from '@/lib/dashboardStream'
import { clockTime } from '@/lib/onlineOrderTiming'
import { amountDue, FAILED_OUTCOME_LABEL } from '@/lib/riderLinks'
import { TicketClock } from '@/components/rider/RiderPage'
import { HandinDialog } from '@/components/dispatch/HandinDialog'
import { PinDialog } from '@/components/dispatch/PinDialog'
import type { DispatchBoard, DispatchRider, RiderTicket } from '@/types/rider'

// Rider Management (F-DLV-N1): the counter's dispatch board. Every delivery order of
// the open day that hasn't reached the customer — storefront and counter/phone
// alike — most urgent first, with its rider; the riders on shift with their
// load and the money they hold; and the hand-in.

export const DISPATCH_BOARD_QUERY_KEY = ['dispatch', 'board'] as const

const STEP_LABEL: Record<string, string> = {
  assigned: 'Assigned',
  picked_up: 'On the way',
  arrived: 'At the door',
}

// The order's own status, in the words the counter uses. A paid order is
// "Paid" here, not "completed" — on this board the food may still be out.
const ORDER_STATUS_LABEL: Record<string, string> = {
  pending: 'Not started',
  confirmed: 'In the kitchen',
  preparing: 'In the kitchen',
  ready: 'Ready',
  out_for_delivery: 'Out for delivery',
  completed: 'Paid',
}

function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return now
}

export function DispatchPage() {
  const qc = useQueryClient()
  const now = useNow()
  const { formatCurrency } = useCurrency()
  const [handinRider, setHandinRider] = useState<DispatchRider | null>(null)
  const [view, setView] = useState<'list' | 'map'>('list')
  const [pinTarget, setPinTarget] = useState<RiderTicket | null>(null)

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: DISPATCH_BOARD_QUERY_KEY,
    queryFn: () => apiClient.getDispatchBoard(),
    // Riders send their position every 15s; the map follows at 10s.
    refetchInterval: view === 'map' ? 10_000 : 15_000,
  })
  const board = data?.data as DispatchBoard | undefined

  const onEvent = useCallback(
    (ev: DashboardStreamEvent) => {
      if (ev.type === 'dispatch_updated' || ev.type.startsWith('order_') || ev.type.startsWith('online_order_')) {
        qc.invalidateQueries({ queryKey: DISPATCH_BOARD_QUERY_KEY })
      }
    },
    [qc],
  )
  useDashboardStream({ onEvent })

  const refresh = () => qc.invalidateQueries({ queryKey: DISPATCH_BOARD_QUERY_KEY })
  const onError = (title: string) => (err: unknown) =>
    toastHelpers.error(title, (err as { message?: string })?.message || 'Please try again.')

  const assign = useMutation({
    mutationFn: ({ orderId, riderId }: { orderId: string; riderId: string }) => apiClient.assignRider(orderId, riderId),
    onSuccess: refresh,
    onError: onError('Could not assign rider'),
  })
  const unassign = useMutation({
    mutationFn: (orderId: string) => apiClient.unassignRider(orderId),
    onSuccess: refresh,
    onError: onError('Could not unassign'),
  })
  const delivered = useMutation({
    mutationFn: (orderId: string) => apiClient.markDeliveredWithoutRider(orderId),
    onSuccess: () => {
      toastHelpers.success('Marked delivered')
      refresh()
    },
    onError: onError('Could not mark delivered'),
  })

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading deliveries…</div>
  if (isError || !board) {
    return (
      <div className="flex flex-col items-center gap-3 p-8 text-center">
        <p className="text-sm text-muted-foreground">Couldn’t load deliveries.</p>
        <Button variant="outline" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    )
  }

  const unassignedCount = board.open.filter((t) => !t.assignment_id).length

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div>
          <h1 className="text-lg font-semibold">Rider Management</h1>
          <p className="text-xs text-muted-foreground">
            {board.open.length} open · {unassignedCount} without a rider · {board.delivered.length} delivered today
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={view} onValueChange={(v) => setView(v as 'list' | 'map')}>
            <TabsList>
              <TabsTrigger value="list">List</TabsTrigger>
              <TabsTrigger value="map">Live map</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section className="min-w-0 space-y-2.5">
          {view === 'map' ? (
            <DispatchMap board={board} now={now} />
          ) : board.open.length === 0 ? (
            <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              No deliveries waiting.
            </p>
          ) : (
            board.open.map((t) => (
              <DispatchCard
                key={t.order_id}
                ticket={t}
                now={now}
                riders={board.riders}
                formatCurrency={formatCurrency}
                busy={assign.isPending || unassign.isPending || delivered.isPending}
                onAssign={(riderId) => assign.mutate({ orderId: t.order_id, riderId })}
                onUnassign={() => unassign.mutate(t.order_id)}
                onDelivered={() => delivered.mutate(t.order_id)}
                onPin={() => setPinTarget(t)}
              />
            ))
          )}

          {board.delivered.length > 0 && (
            <details className="pt-2">
              <summary className="cursor-pointer text-sm font-semibold">
                Delivered today <span className="font-normal text-muted-foreground">({board.delivered.length})</span>
              </summary>
              <ul className="mt-2 divide-y rounded-lg border bg-card text-sm">
                {board.delivered.map((t) => (
                  <li key={t.assignment_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                    <span>
                      #{t.order_number} · {t.customer_name || 'Customer'}
                      {t.outcome_distance_m != null && t.outcome_distance_m > board.far_delivery_m && (
                        <Badge
                          variant="outline"
                          className="ml-2 border-red-500/50 text-[10px] text-red-700 dark:text-red-400"
                        >
                          Tapped {formatDistance(t.outcome_distance_m)} from the pin
                        </Badge>
                      )}
                      {t.outcome === 'delivered_with_issue' && (
                        <Badge
                          variant="outline"
                          className="ml-2 border-amber-500/50 text-[10px] text-amber-700 dark:text-amber-400"
                        >
                          Issue: {t.issue}
                        </Badge>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t.rider_name} · {clockTime(t.finished_at)}
                      {t.picked_up_at && t.finished_at && (
                        <>
                          {' '}
                          ·{' '}
                          {Math.max(
                            1,
                            Math.round(
                              (new Date(t.finished_at).getTime() - new Date(t.picked_up_at).getTime()) / 60_000,
                            ),
                          )}
                          m on the road
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>

        <aside className="space-y-2.5">
          <h2 className="text-sm font-semibold">Riders</h2>
          {board.riders.length === 0 ? (
            <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              No riders yet. Add staff with the Rider role under Staff.
            </p>
          ) : (
            board.riders.map((r) => (
              <div key={r.id} className="rounded-lg border bg-card p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-2 font-medium">
                    <span
                      className={`h-2 w-2 rounded-full ${r.on_shift ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`}
                      aria-hidden
                    />
                    {r.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {r.on_shift ? `since ${clockTime(r.shift_started_at)}` : 'off shift'}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {r.live} assigned · {r.on_road} on the road · {r.delivered_today} delivered today
                </p>
                {r.on_shift && <RiderSeen rider={r} now={now} />}
                {r.held.count > 0 && (
                  <div className="mt-2 flex items-center justify-between gap-2 rounded-md bg-amber-50 px-2 py-1.5 dark:bg-amber-950/40">
                    <span className="inline-flex items-center gap-1.5 text-xs text-amber-900 dark:text-amber-200">
                      <Wallet className="h-3.5 w-3.5" aria-hidden />
                      Holds {formatCurrency(r.held.cash + r.held.wallet)}
                      {r.held.wallet > 0 && ` (${formatCurrency(r.held.wallet)} wallet)`}
                    </span>
                    <Button size="sm" variant="outline" onClick={() => setHandinRider(r)}>
                      Hand-in
                    </Button>
                  </div>
                )}
              </div>
            ))
          )}
        </aside>
      </div>

      <HandinDialog rider={handinRider} onClose={() => setHandinRider(null)} />
      {pinTarget && (
        <PinDialog
          open
          orderId={pinTarget.order_id}
          orderNumber={pinTarget.order_number}
          address={pinTarget.delivery_address}
          initial={pinTarget.lat != null && pinTarget.lng != null ? { lat: pinTarget.lat, lng: pinTarget.lng } : null}
          suggested={pinTarget.suggested_pin ?? null}
          onClose={() => setPinTarget(null)}
        />
      )}
    </div>
  )
}

function formatDistance(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`
}

const SEEN_TEXT = {
  live: 'text-emerald-700 dark:text-emerald-400',
  stale: 'text-amber-700 dark:text-amber-400',
  lost: 'text-red-700 dark:text-red-400',
} as const

/** "Location live · seen 20s ago · 64%" — or why the counter can't see them. */
function RiderSeen({ rider, now }: { rider: DispatchRider; now: number }) {
  const at = rider.location ? new Date(rider.location.recorded_at).getTime() : null
  const fresh = locationFreshness(at, now)
  return (
    <p className={`mt-0.5 text-xs ${SEEN_TEXT[fresh]}`}>
      {at == null ? 'No location shared yet' : `Location seen ${lastSeenLabel(at, now)}`}
      {rider.location?.battery != null && (
        <span
          className={
            rider.location.battery < 0.15 ? ' font-medium text-red-700 dark:text-red-400' : ' text-muted-foreground'
          }
        >
          {' '}
          · battery {Math.round(rider.location.battery * 100)}%
        </span>
      )}
    </p>
  )
}

function DispatchCard({
  ticket,
  now,
  riders,
  formatCurrency,
  busy,
  onAssign,
  onUnassign,
  onDelivered,
  onPin,
}: {
  ticket: RiderTicket
  now: number
  riders: DispatchRider[]
  formatCurrency: (n: number) => string
  busy: boolean
  onAssign: (riderId: string) => void
  onUnassign: () => void
  onDelivered: () => void
  onPin: () => void
}) {
  const due = amountDue(ticket.total, ticket.paid)
  const itemCount = ticket.items.reduce((n, i) => n + i.quantity, 0)
  const onRoad = ticket.assignment_status === 'picked_up' || ticket.assignment_status === 'arrived'

  return (
    <div className={`rounded-lg border bg-card p-3 shadow-sm ${ticket.assignment_id ? '' : 'border-amber-400/70'}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-semibold">#{ticket.order_number}</span>
          <Badge variant="secondary" className="text-[10px]">
            {ticket.order_source === 'online' ? 'Online' : 'Counter'}
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            {ORDER_STATUS_LABEL[ticket.order_status] ?? ticket.order_status.replace(/_/g, ' ')}
          </Badge>
          {ticket.assignment_status && (
            <Badge className="text-[10px]">
              <Bike className="mr-1 h-3 w-3" aria-hidden />
              {STEP_LABEL[ticket.assignment_status] ?? ticket.assignment_status}
            </Badge>
          )}
        </div>
        <TicketClock ticket={ticket} now={now} />
      </div>

      {ticket.last_failure && !ticket.assignment_id && (
        <p className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-red-50 px-2 py-1 text-xs text-red-800 dark:bg-red-950/40 dark:text-red-200">
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
          Returned by {ticket.last_failure.rider_name}: {FAILED_OUTCOME_LABEL[ticket.last_failure.outcome]}
          {ticket.last_failure.note ? ` — “${ticket.last_failure.note}”` : ''}
        </p>
      )}

      <div className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
        <p className="flex items-start gap-1.5">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span>
            <span className="line-clamp-2">{ticket.delivery_address || 'No address'}</span>
            <button
              type="button"
              onClick={onPin}
              className={`text-xs underline-offset-2 hover:underline ${
                ticket.lat != null
                  ? 'text-emerald-700 dark:text-emerald-400'
                  : ticket.suggested_pin
                    ? 'text-primary'
                    : 'font-medium text-amber-700 dark:text-amber-400'
              }`}
            >
              {ticket.lat != null ? 'Pin set · change' : ticket.suggested_pin ? 'Pin from last time · use it' : 'No map pin · set pin'}
            </button>
          </span>
        </p>
        <p className="flex items-center gap-1.5 text-muted-foreground">
          <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {ticket.customer_name || 'Customer'} {ticket.customer_phone ? `· ${ticket.customer_phone}` : ''}
        </p>
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t pt-2">
        <span className="text-xs text-muted-foreground">
          {itemCount} item{itemCount === 1 ? '' : 's'} · {formatCurrency(ticket.total)} ·{' '}
          {due > 0 ? <span className="font-medium text-foreground">{formatCurrency(due)} to collect</span> : 'paid'}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={ticket.rider_id ?? ''}
            onValueChange={(v) => v && v !== ticket.rider_id && onAssign(v)}
            disabled={busy}
          >
            <SelectTrigger className="h-8 w-48 text-sm">
              <SelectValue placeholder="Assign rider" />
            </SelectTrigger>
            <SelectContent>
              {riders.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name} · {r.on_shift ? `${r.live} assigned` : 'off shift'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {ticket.assignment_id ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                if (!onRoad || window.confirm('The rider has already left with this order. Take it back from them?'))
                  onUnassign()
              }}
            >
              Unassign
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={onDelivered}
              title="Delivered by someone not using the rider app"
            >
              Delivered (no rider)
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
